import { computed, ref, watch } from 'vue';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { storeToRefs } from 'pinia';
import { useTimePeriodStore, type InitTimeOption } from '@/common/stores/main-view-time-period';
import type {
  TimePeriodInfo,
  TimePeriodOptions,
  Mode,
} from '@/common/components/molecules/timeController/types';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import type { TimePeriodData } from '@/types/common.types';
import TimeController from '@/common/components/molecules/timeController/TimeController.vue';
import { getInitPeriodInfo } from '@/common/components/molecules/timeController/utils';
import { AlertInfoType } from '@/common/components/organisms/overviewAlert/overviewAlert.setup';

export interface Props {
  usePath?: boolean;
  useTimePeriod?: boolean;
  timePeriodOptions?: TimePeriodOptions;
  timePeriodMode?: Mode;
  timePeriodItems?: Readonly<TimePeriodData[]>;
  initTimeOption?: InitTimeOption;
  customPath?: string;
  alertInfo?: AlertInfoType;
}
export interface Emit {
  (e: 'update-time-period', value: TimePeriodInfo): void;
}

export const useGlobalTime = (props?: Props) => {
  const baseMenuStore = useBaseMenuStore();
  const { menuVisitRoute } = storeToRefs(baseMenuStore);

  const timePeriodStore = useTimePeriodStore();
  const { timePeriodInfo } = storeToRefs(timePeriodStore);
  const { updateTimePeriod, initTimePeriod } = timePeriodStore;

  const currentRoute = computed<MenuInfo | undefined>(() => menuVisitRoute.value.at(-1));
  const path = computed(() => props?.customPath || currentRoute.value?.path);

  const selectedPeriodInfo = computed<TimePeriodInfo>(() => {
    // 스토어 미초기화(특히 customPath 화면) 시 {} 대신 유효한 기본 TimePeriodInfo 를 반환.
    // {} 는 fromTime/toTime 이 undefined 라 TimePeriodDisplay 에서 크래시한다 (DSP-44238).
    return timePeriodInfo.value[path.value ?? ''] ?? getInitPeriodInfo(props?.initTimeOption);
  });
  const updatePeriodInfo = (info: TimePeriodInfo) => {
    if (path.value && info) {
      updateTimePeriod(path.value, info);
    }
  };

  return {
    timePeriodInfo,
    menuVisitRoute,
    currentRoute,
    path,
    selectedPeriodInfo,
    updatePeriodInfo,
    initTimePeriod,
  };
};

const setup = (props: Props, emit: Emit) => {
  const {
    currentRoute,
    menuVisitRoute,
    timePeriodInfo,
    path,
    selectedPeriodInfo,
    updatePeriodInfo,
    initTimePeriod,
  } = useGlobalTime(props);
  const timeIndicatorRef = ref<InstanceType<typeof TimeController> | null>(null);

  const onUpdatePeriodInfo = (info: TimePeriodInfo) => {
    if (info) {
      updatePeriodInfo(info);
      emit('update-time-period', info);
    }
  };

  watch(
    menuVisitRoute,
    () => {
      // customPath 가 있으면 그 키로 초기화해야 selectedPeriodInfo(= path.value 기준)와 정합한다.
      const currentPath = path.value;
      if (currentPath && !timePeriodInfo.value[currentPath] && props.useTimePeriod) {
        initTimePeriod(currentPath, props.initTimeOption);
      }
    },
    { immediate: true },
  );

  const routeDisplayText = computed<string>(() => currentRoute.value?.text ?? '');

  return {
    timeIndicatorRef,
    currentRoute,
    selectedPeriodInfo,
    routeDisplayText,
    onUpdatePeriodInfo,
  };
};

export { setup };
